/**
 * Vocabulário de tela do monitorador — o que cada estado SIGNIFICA para quem
 * opera, e qual ação resolve.
 *
 * Fica separado da tela por dois motivos: o teste consegue afirmar sobre os
 * rótulos sem montar a página inteira, e o mesmo texto serve a lista, ao
 * detalhe e ao drawer de QR sem ninguém reescrever "Instável" de cabeça.
 *
 * Nada aqui decide regra de negócio: o servidor já mandou `status`, `health` e
 * o limite do plano avaliado. Isto é tradução, não cálculo.
 */
import type { MonitorStatus } from '@mgdlead/ui';
import { MONITOR_STATUS } from '@mgdlead/ui';
import type { Monitor } from '../../lib/api';

export type MonitorHealthValue = Monitor['health'];

/** Os 6 estados na ordem em que fazem sentido contar a história da conexão. */
export const MONITOR_STATUSES: readonly MonitorStatus[] = [
  'CONNECTED',
  'DEGRADED',
  'CONNECTING',
  'QR_REQUIRED',
  'DISCONNECTED',
  'ERROR',
];

/** Rótulo textual do estado — o mesmo do `StatusPill`, para não existirem dois. */
export function monitorStatusLabel(status: MonitorStatus): string {
  return MONITOR_STATUS[status].label;
}

/**
 * Uma frase por estado, em português de gente. Quem abre esta tela não sabe o
 * que é "sessão", "provider" nem "webhook" — e não precisa saber.
 */
export const MONITOR_STATUS_HINT: Record<MonitorStatus, string> = {
  CONNECTED: 'O número está conectado e recebendo as movimentações dos grupos.',
  CONNECTING: 'A conexão está subindo. Costuma levar alguns segundos.',
  DISCONNECTED: 'O número não está conectado. Nenhuma movimentação está sendo registrada.',
  ERROR: 'A conexão falhou. Veja o último erro e tente conectar de novo.',
  QR_REQUIRED: 'Falta ler o QR code no celular para o número entrar no ar.',
  DEGRADED:
    'Conectado, mas sem receber movimentação há tempo demais. Normalmente é sinal de que o aviso automático do WhatsApp para o MGDLead parou de chegar.',
};

export interface HealthSpec {
  label: string;
  tone: 'success' | 'warning' | 'danger';
  description: string;
}

/**
 * `health` responde outra pergunta que `status`: "isso está capturando lead?".
 * Por isso tem rótulo próprio — repetir "Conectado" nas duas colunas esconderia
 * justamente o caso que interessa (conectado E mudo).
 */
export const MONITOR_HEALTH: Record<MonitorHealthValue, HealthSpec> = {
  OK: {
    label: 'Capturando',
    tone: 'success',
    description: 'Entradas e saídas dos grupos estão chegando normalmente.',
  },
  DEGRADED: {
    label: 'Instável',
    tone: 'warning',
    description: 'Está no ar, mas alguma coisa impede a captura de acontecer como deveria.',
  },
  DOWN: {
    label: 'Sem captura',
    tone: 'danger',
    description: 'Nada está sendo capturado enquanto isso não for resolvido.',
  },
};

/**
 * A ação de conexão que cabe em cada estado. UMA por linha — oferecer
 * "Conectar", "Reconectar" e "Desconectar" ao mesmo tempo é o jeito mais rápido
 * de alguém derrubar um número que estava funcionando.
 *
 * Esconder não é autorizar: o servidor recusa de qualquer forma. Isto é a
 * mesma leitura de `canRemove` na tela de membros.
 */
export type MonitorConnectionAction = 'connect' | 'qr' | 'reconnect' | 'disconnect';

export const CONNECTION_ACTION_LABEL: Record<MonitorConnectionAction, string> = {
  connect: 'Conectar',
  qr: 'Ver QR',
  reconnect: 'Reconectar',
  disconnect: 'Desconectar',
};

export function connectionActionFor(status: MonitorStatus): MonitorConnectionAction {
  switch (status) {
    case 'DISCONNECTED':
      return 'connect';
    // Em ambos o QR já é (ou está prestes a ser) o próximo passo do usuário.
    case 'QR_REQUIRED':
    case 'CONNECTING':
      return 'qr';
    case 'ERROR':
      return 'reconnect';
    case 'CONNECTED':
    case 'DEGRADED':
      return 'disconnect';
  }
}

/* -------------------------------------------------------------------------- */
/*  Excluir: o que ainda segura o registro                                     */
/* -------------------------------------------------------------------------- */

/**
 * Por que a exclusão definitiva ainda NÃO está disponível — ou `null` quando está.
 *
 * Uma regra só, e não é invenção da tela: **arquivar vem antes.** `removeMonitor` recusa
 * com `CONFLICT` quem não foi arquivado. O passo a mais existe para dar chance de perceber
 * o engano — e `monitor` não tem `unarchive`, então o diálogo diz isso antes do clique.
 *
 * Histórico NÃO bloqueia mais (pedido do dono, 11/10/2026): o servidor exclui o arquivado
 * sem tocar em grupo, participação ou evento — quando há histórico, o monitorador só sai
 * de cena e o histórico fica inteiro (ver `removeMonitor`, migration 0039).
 */
export function monitorRemovalBlocker(monitor: Monitor): string | null {
  if (monitor.archivedAt === null) {
    return 'Arquive antes de excluir: é o passo que dá chance de perceber o engano.';
  }
  return null;
}

/**
 * O arquivado tem histórico preso? Lê só o que a linha já traz do servidor (`groupsCount`,
 * `lastEventAt`) — serve para a tela tranquilizar ANTES do clique: excluir não apaga nada
 * do que ele registrou.
 */
export function monitorHasHistory(monitor: Monitor): boolean {
  return monitor.groupsCount > 0 || monitor.lastEventAt !== null;
}

/* -------------------------------------------------------------------------- */
/*  QR                                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Os 5 estados que `monitor.qr` devolve, cada um com a frase que o usuário lê.
 *
 * `PENDING` **não é erro** — é a sessão subindo. Tratar os dois igual faria a
 * tela gritar "falhou" no segundo normal de espera de toda conexão.
 */
export const QR_STATE_TITLE = {
  QR: 'Leia o código com o seu WhatsApp',
  ALREADY_CONNECTED: 'Este número já está conectado',
  PENDING: 'Preparando a conexão…',
  NOT_STARTED: 'A conexão não está ativa',
  EXPIRED: 'O código expirou',
  ERROR: 'Não foi possível pegar o QR code',
} as const;

export const QR_STATE_MESSAGE = {
  QR: 'O código vale por poucos segundos e é trocado sozinho enquanto esta janela estiver aberta.',
  ALREADY_CONNECTED: 'Não é preciso ler nada. Pode fechar esta janela.',
  PENDING: 'O QR code aparece aqui em alguns segundos. Deixe a janela aberta.',
  NOT_STARTED:
    'A conexão foi parada ou o número saiu do ar. Esperar não resolve: é preciso conectar de novo.',
  /**
   * O caso mais comum de quem abre a tela e vai buscar o celular. Dizer "o código expirou"
   * é honesto e devolve a ação para quem lê; "a sessão falhou no provider" fala de uma
   * engrenagem que não é problema dela.
   */
  EXPIRED:
    'O código do WhatsApp vale por pouco tempo e este passou do prazo sem ser lido. Gere outro e deixe o celular em mãos: dá uns 20 segundos por código.',
  ERROR: 'Falhamos ao falar com o serviço que gera o código.',
} as const;

/** Passo a passo que quem nunca conectou um aparelho precisa ler. */
export const QR_INSTRUCTIONS: readonly string[] = [
  'Abra o WhatsApp no celular que vai monitorar os grupos.',
  'Toque em Configurações e depois em Aparelhos conectados.',
  'Toque em Conectar um aparelho e confirme com a sua digital ou senha.',
  'Aponte a câmera do celular para o código desta tela.',
];

/* -------------------------------------------------------------------------- */
/*  Resumo dos monitoradores para o rodapé da barra lateral                     */
/* -------------------------------------------------------------------------- */

/**
 * O estado agregado dos números, em uma linha.
 *
 * O rodapé nasceu na Fase 1 com o texto **fixo** "Nenhum monitor conectado" e um comentário
 * dizendo "enquanto a Fase 2 não existe, o rodapé diz a verdade em vez de inventar". A Fase 2
 * chegou, os números conectaram — e a frase ficou, dizendo em TODAS as telas o contrário do
 * que o banco sabia. Provisório que não tem teste não é provisório: é o texto definitivo.
 *
 * A ordem de desempate quando ninguém está conectado é a da AÇÃO que resolve, não a do
 * alfabeto: quem precisa de QR primeiro, porque é o único estado que depende de alguém pegar
 * o celular.
 */
const ORDEM_DE_ATENCAO: readonly MonitorStatus[] = [
  'QR_REQUIRED',
  'ERROR',
  'CONNECTING',
  'DEGRADED',
  'DISCONNECTED',
];

export function monitorsSummary(monitors: readonly Monitor[]): {
  status: MonitorStatus;
  label: string;
} {
  if (monitors.length === 0) {
    return { status: 'DISCONNECTED', label: 'Nenhum número conectado' };
  }

  const conectados = monitors.filter((monitor) => monitor.status === 'CONNECTED').length;

  if (conectados === monitors.length) {
    return {
      status: 'CONNECTED',
      label: conectados === 1 ? 'Número conectado' : `${conectados} números conectados`,
    };
  }

  /**
   * Parcial é o caso que mais engana: dizer só "conectado" com 1 de 3 no ar esconde dois
   * números mudos, e cada número mudo é um grupo inteiro sem registro de entrada e saída.
   */
  if (conectados > 0) {
    return { status: 'DEGRADED', label: `${conectados} de ${monitors.length} conectados` };
  }

  const pior =
    ORDEM_DE_ATENCAO.find((status) => monitors.some((monitor) => monitor.status === status)) ??
    'DISCONNECTED';

  return {
    status: pior,
    label: pior === 'QR_REQUIRED' ? 'Falta escanear o QR' : 'Nenhum número conectado',
  };
}
